import { isDisplayTool } from '../constants/displayTools';
import TOOL_ICONS, { DEFAULT_TOOL_ICON } from '../constants/toolIcons';
import useAiChatToolLabel from '../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../hooks/useAiChatToolStatus';
import readToolComponent from '../utils/readToolComponent';
import AiChatToolHeader from './AiChatToolHeader';
import ChatStepIcon from './ChatStepIcon';

import type { ReactElement } from 'react';
import type IAiChatToolCall from '../interfaces/IAiChatToolCall';

import styles from '../sass/AiChat.module.scss';

/**
 * One tool call of a reply, listed by name.
 *
 * A tool with a component of its own renders that; every other tool renders as its label and what
 * the call is doing, so the steps of a reply can be read without the raw JSON. The input and the
 * output are deliberately not printed here: only a component that knows the tool knows how to
 * show them.
 *
 * A display tool is the exception to the row: its component is the output, placed inline in the
 * reply, so it gets no step row, no icon and no connector. Only when it showed nothing does it draw
 * a step row of its own (`AiChatDisplayFailed`): a failed display is a step the model goes on from.
 */
function AiChatToolCall({ part, name }: IAiChatToolCall): ReactElement {
  const CustomToolCall = readToolComponent(part, name);
  const label = useAiChatToolLabel(name);
  const status = useAiChatToolStatus(part.state);
  const StepIcon = TOOL_ICONS[name] || DEFAULT_TOOL_ICON;

  if (CustomToolCall && isDisplayTool(name)) {
    return <CustomToolCall part={part} />;
  }

  // The icon sits in the row, outside whatever the call itself draws, so every step is marked the
  // same way — a tool with a component of its own included.
  return (
    <div className={styles.stepRow}>
      <ChatStepIcon icon={<StepIcon />} />

      {CustomToolCall
        ? <CustomToolCall part={part} />
        : <AiChatToolHeader name={label} status={status} />}
    </div>
  );
}

export default AiChatToolCall;
